import React, { useCallback, useEffect } from "react";
import { commitMutation, graphql, useFragment } from "react-relay";
import { useHistory, useRouteMatch } from "react-router";

import useCurrentLibraryChannel from "~/lib/hooks/useCurrentLibraryChannel";
import { useMutationPromise } from "~/lib/relayUtil";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { getFirstUserFacingError } from "~/lib/utils";
import { environment } from "~/models";
import { useLibraryNavigationFeatureFlagOn } from "~/nav/featureFlag";
import { useLibraryQueryParams } from "~/recordings/Filters/Filters";
import { useModals } from "~/ui";
import toast from "~/ui/toast";

import BrowseChannelsModal, {
	type BrowseChannelType,
	joinChannelMutation,
	leaveChannelMutation,
	leaveConfirmation,
} from "./BrowseChannelsModal/BrowseChannelsModal";
import { analyticsProperties } from "./constants";
import { eventEmitter as recordingsEventEmitter } from "./events";

import type { BrowseChannelsModalJoinChannelMutation } from "~/__generated__/BrowseChannelsModalJoinChannelMutation.graphql";
import type { BrowseChannelsModalLeaveChannelMutation } from "~/__generated__/BrowseChannelsModalLeaveChannelMutation.graphql";
import type { LibraryHooksUseLeaveChannel_viewer$key } from "~/__generated__/LibraryHooksUseLeaveChannel_viewer.graphql";
import type { LibraryHooksUseOpenChannelsModal_viewer$key } from "~/__generated__/LibraryHooksUseOpenChannelsModal_viewer.graphql";
import type { LibraryHooksWatchChannelMutation } from "~/__generated__/LibraryHooksWatchChannelMutation.graphql";

export const useLibraryFeatureRedirection = () => {
	const isLibraryFeatureOn = useLibraryNavigationFeatureFlagOn();
	const history = useHistory();
	const isOnNewUrl = useRouteMatch("/library/");
	const isOnOldUrl = useRouteMatch({
		path: "/recordings/",
		exact: true,
	});

	useEffect(() => {
		if (isLibraryFeatureOn && isOnOldUrl) {
			history.replace("/library/");
		} else if (!isLibraryFeatureOn && isOnNewUrl) {
			history.replace("/recordings/");
		}
	}, [isLibraryFeatureOn, isOnNewUrl, isOnOldUrl, history]);
};

const useLeaveChannelViewerFragment = graphql`
	fragment LibraryHooksUseLeaveChannel_viewer on Viewer {
		autoPublishRecordingSettings {
			id
			enabled
			externalRecordingsChannel {
				id
			}
			internalRecordingsChannel {
				id
			}
		}
	}
`;

export type UseLeaveChannelProps = {
	origin: string;
	viewer: LibraryHooksUseLeaveChannel_viewer$key;
	channel: { id: string; name: string; isPrivate: boolean } | null;
	redirectToLibrary: boolean;
};

export const useLeaveChannel = ({ origin, viewer: viewerKey, channel, redirectToLibrary }: UseLeaveChannelProps) => {
	const history = useHistory();
	const { autoPublishRecordingSettings: autoPublishSettings } = useFragment(useLeaveChannelViewerFragment, viewerKey);

	const leaveChannel = useCallback(async () => {
		if (!channel || !channel?.id || !channel?.name) return;

		const isUsedInAutoPublish = Boolean(
			autoPublishSettings?.enabled &&
				(autoPublishSettings.externalRecordingsChannel?.id === channel.id ||
					autoPublishSettings.internalRecordingsChannel?.id === channel.id),
		);
		if (!(await leaveConfirmation({ isPrivate: channel.isPrivate ?? false, name: channel.name }, isUsedInAutoPublish)))
			return;

		commitMutation<BrowseChannelsModalLeaveChannelMutation>(environment, {
			mutation: leaveChannelMutation,
			variables: { channelId: channel.id },
			onCompleted: () => {
				toast.success("Left the channel!");
				recordingsEventEmitter.emit("Channels.refetch");
				dispatchAnalyticsTrack("Leave channel", { location: origin });
				if (redirectToLibrary) history.push("/library");
			},
		});
	}, [channel, history, autoPublishSettings, redirectToLibrary, origin]);

	return leaveChannel;
};

export type UseJoinChannelProps = {
	origin: string;
	channelId: string;
	redirectToLibrary: boolean;
};

export const useJoinChannel = ({ origin, channelId, redirectToLibrary }: UseJoinChannelProps) => {
	const history = useHistory();

	const joinChannel = useCallback(async () => {
		commitMutation<BrowseChannelsModalJoinChannelMutation>(environment, {
			mutation: joinChannelMutation,
			variables: { channelId },
			onCompleted: _ => {
				toast.success("Joined the channel!");
				recordingsEventEmitter.emit("Channels.refetch");
				dispatchAnalyticsTrack("Join channel", { location: origin });
				if (redirectToLibrary) history.push(`/library/c/${channelId}/`);
			},
		});
	}, [channelId, history, redirectToLibrary, origin]);

	return joinChannel;
};

const useOpenChannelsModalViewerFragment = graphql`
	fragment LibraryHooksUseOpenChannelsModal_viewer on Viewer {
		account {
			billingAccount {
				canCreateChannels: hasAccessToFeature(feature: channels)
			}
		}
	}
`;

export const useChannelsRefetchEventDispatcher = () => {
	return useCallback(() => {
		recordingsEventEmitter.emit("Channels.refetch");
	}, []);
};

export const useOpenChannelsModal = (viewerKey: LibraryHooksUseOpenChannelsModal_viewer$key, location: string) => {
	const modals = useModals();
	const history = useHistory();
	const {
		account: {
			billingAccount: { canCreateChannels },
		},
	} = useFragment(useOpenChannelsModalViewerFragment, viewerKey);

	const onBrowseChannelSelected = React.useCallback(
		(channel: BrowseChannelType) => history.push(`/library/c/${channel.id}/`),
		[history],
	);

	const openChannelsModal = useCallback(() => {
		dispatchAnalyticsTrack("Open all channels modal", { location });
		modals.open(({ close }) => (
			<BrowseChannelsModal
				onClose={close}
				canCreateChannels={canCreateChannels}
				selectChannel={onBrowseChannelSelected}
				redirectToLibrary={true}
			/>
		));
	}, [modals, canCreateChannels, onBrowseChannelSelected, location]);

	return openChannelsModal;
};

export function useWatchChannelMutation() {
	const [commit, isInFlight] = useMutationPromise<LibraryHooksWatchChannelMutation>(graphql`
		mutation LibraryHooksWatchChannelMutation($channelId: ID!, $value: Boolean!) @raw_response_type {
			channelSetWatching(channelId: $channelId, value: $value) {
				channel {
					id
					viewerWatching
				}
			}
		}
	`);

	const setWatching = React.useCallback(
		async (channelId: string, channelName: string, value: boolean) => {
			try {
				await commit({
					variables: { channelId, value },
					optimisticResponse: {
						channelSetWatching: {
							channel: { id: channelId, viewerWatching: value },
						},
					},
				});

				if (value) {
					toast.success(`Watching ${channelName} channel`);
				} else {
					toast.success(`${channelName} channel unwatched`);
				}
			} catch (errors) {
				const userFacingError = getFirstUserFacingError(errors) ?? "Something went wrong, please try again.";
				toast.error(userFacingError, { title: "Failed to save settings" });
			}
		},
		[commit],
	);

	return [setWatching, isInFlight] as const;
}

export const useDispatchAnalyticsOnPageOpen = (location: string) => {
	const { name } = useCurrentLibraryChannel();
	const { companyDomain } = useLibraryQueryParams();

	useEffect(() => {
		dispatchAnalyticsTrack("Recordings Page Opened", {
			location,
			fingerprint: analyticsProperties.fingerprint,
			channelName: name,
			hasCompanyFilter: !!companyDomain,
		});
	}, []); // eslint-disable-line react-hooks/exhaustive-deps
};
